import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import AuroraBackground from '../components/AuroraBackground';
import GoldText from '../components/GoldText';

type Stage = 'credentials' | '2fa' | 'non-admin-success';

export default function Login() {
  const navigate = useNavigate();
  const { login, logout } = useAuth();

  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAuthSuccess = (data: any) => {
    const userObj = {
      id: data.id ?? data.userId,
      email: data.email ?? email,
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      isAdmin: !!data.isAdmin,
    };
    login({ accessToken: data.accessToken, refreshToken: data.refreshToken }, userObj);

    if (userObj.isAdmin) {
      navigate('/admin');
    } else {
      setStage('non-admin-success');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });

      if (data.requires_2fa) {
        setSessionToken(data.session_token);
        setStage('2fa');
        setLoading(false);
        return;
      }

      handleAuthSuccess(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to sign in. Please check your credentials.');
      setLoading(false);
    }
  };

  const handle2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-2fa', { sessionToken, code });
      const me = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${data.accessToken}` },
      });
      handleAuthSuccess({ ...data, ...me.data });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid authentication code.');
      setLoading(false);
    }
  };

  if (stage === 'non-admin-success') {
    return (
      <div className="relative min-h-screen bg-bg flex items-center justify-center px-4 overflow-hidden">
        <AuroraBackground />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative w-full max-w-md bg-surface1 border border-border rounded-2xl shadow-2xl p-8 text-center"
        >
          <ShieldCheck className="mx-auto text-gold mb-4" size={48} />
          <h1 className="font-display text-2xl font-bold text-textPrimary mb-2">You're signed in</h1>
          <p className="text-textSecondary mb-6">
            The member dashboard on the web isn't ready yet — Bankably is mobile-first. Use the Bankably
            mobile app for your full account experience.
          </p>
          <button
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="w-full py-3 bg-gold-gradient text-bg font-bold rounded-lg hover:opacity-90 transition"
          >
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-bg flex items-center justify-center px-4 overflow-hidden">
      <AuroraBackground />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="bg-surface1 border border-border rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold"><GoldText>Bankably</GoldText></h1>
            <p className="text-textSecondary mt-2">Gold-Backed Banking</p>
          </div>

          {stage === 'credentials' && (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-textPrimary font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-surface2 text-textPrimary border border-border focus:border-gold focus:outline-none"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="mb-2">
                <label className="block text-textPrimary font-semibold mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-surface2 text-textPrimary border border-border focus:border-gold focus:outline-none"
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && <p className="text-error text-sm mt-2 mb-2">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-4 py-2 bg-gold-gradient text-bg font-bold rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight size={18} />}
              </button>
            </form>
          )}

          {stage === '2fa' && (
            <form onSubmit={handle2fa}>
              <p className="text-textSecondary text-sm mb-4">
                Enter the 6-digit code from your authenticator app.
              </p>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                className="w-full px-4 py-2 rounded-lg bg-surface2 text-textPrimary border border-border focus:border-gold focus:outline-none text-center text-2xl tracking-widest"
                placeholder="000000"
                required
              />
              {error && <p className="text-error text-sm mt-2">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-4 py-2 bg-gold-gradient text-bg font-bold rounded-lg hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <p className="text-textSecondary">
              Don't have an account? Bankably is mobile-first — sign up on our mobile app (coming soon).
            </p>
            <Link to="/wishlist" className="text-gold font-semibold hover:text-goldLight inline-block mt-2">
              Join the waitlist to get notified →
            </Link>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-textTertiary hover:text-textPrimary text-sm">
              ← Back to Home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
